import { Text, View } from '@react-pdf/renderer'
import {
  LAYOUT,
  MATH_CONSTANTS,
  MEASUREMENT_FORMAT,
  REPORT_COLORS,
  SPACING,
  TYPOGRAPHY,
} from 'pages/reporting/reports/rug/constants/rugReportStyles'
import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import { ComponentId } from 'pages/reporting/reports/rug/types'
import React, { useMemo } from 'react'
import {
  GeckoAssetModelType,
  GeckoComponentModelType,
  GeckoLegacyBoilerWalls,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
  GeckoVerticalTankShell,
} from 'types'

const DataSheetListedItem: React.FC<{
  fieldName: string
  fieldValue?: string
  flexDirection?: 'row' | 'column'
}> = ({ fieldName, fieldValue, flexDirection }) => {
  return (
    <View
      style={{
        flexDirection: flexDirection ?? 'row',
        alignItems: flexDirection === 'column' ? 'flex-start' : 'center',
        minHeight: LAYOUT.HEIGHT.DATA_ITEM_MIN,
        gap: SPACING.GAP.EXTRA_SMALL,
        fontSize: TYPOGRAPHY.FONT_SIZE.SMALL,
      }}>
      <Text
        style={{
          fontWeight: 'bold',
          color: REPORT_COLORS.HEADER_TEXT,
          fontSize: TYPOGRAPHY.FONT_SIZE.EXTRA_SMALL,
        }}>
        {fieldName}
      </Text>
      <Text
        style={{
          color: REPORT_COLORS.VALUE_TEXT,
          fontSize: TYPOGRAPHY.FONT_SIZE.EXTRA_SMALL,
        }}>
        {fieldValue}
      </Text>
    </View>
  )
}

interface ImagePageHeaderProps {
  componentId?: ComponentId
}

interface DataItem {
  label: string
  value?: GeckoMeasurement | string
  formattedValue?: string
}

export const ImagePageHeader: React.FC<ImagePageHeaderProps> = ({
  componentId,
}) => {
  const {
    organization,
    site,
    unit,
    formData,
    selectedAssetModel,
    selectedComponentModels,
    componentToNominalRange,
    formatMeasurement,
    formatMeasurementRange,
  } = useRugReportOps()

  const nominalRange = useMemo(() => {
    if (!componentToNominalRange || componentToNominalRange.size === 0) {
      return undefined
    }

    const ranges = Array.from(componentToNominalRange.values())
    if (ranges.length === 0) return undefined

    const minValues = ranges.map((range) => range.start)
    const maxValues = ranges.map((range) => range.end)

    const overallMin = GeckoMeasurement.Min(...minValues)
    const overallMax = GeckoMeasurement.Max(...maxValues)

    return GeckoMeasurementRange.CreateFromStartEnd(overallMin, overallMax)
  }, [componentToNominalRange])

  const componentModel = componentId
    ? selectedComponentModels?.find((model) => model.id === componentId)
    : selectedComponentModels?.[0]

  const dimensions = useMemo(() => {
    const items: DataItem[] = []

    if (selectedAssetModel?.data.type === GeckoAssetModelType.VerticalTank) {
      const shellComponent = Array.from(
        selectedAssetModel.data.components.values()
      ).find(
        (comp) => comp.type === GeckoComponentModelType.VerticalTankShell
      ) as GeckoVerticalTankShell | undefined

      if (shellComponent) {
        items.push(
          {
            label: 'Diameter:',
            value: shellComponent.radius.scale(
              MATH_CONSTANTS.RADIUS_TO_DIAMETER_SCALE
            ),
          },
          {
            label: 'Height:',
            value: shellComponent.height,
          }
        )
      }
    } else if (
      selectedAssetModel?.data.type === GeckoAssetModelType.LegacyBoiler
    ) {
      const boilerComponent =
        (componentModel as GeckoLegacyBoilerWalls | undefined) ||
        (Array.from(selectedAssetModel.data.components.values()).find(
          (comp) => comp.type === GeckoComponentModelType.LegacyBoilerWalls
        ) as GeckoLegacyBoilerWalls | undefined)

      if (boilerComponent) {
        // Inches selects millimeters for metric reports instead of meters.
        items.push(
          {
            label: 'Tube Diameter:',
            value: boilerComponent.tubeDiameter?.convertTo(
              GeckoMeasurementUnit.Inches
            ),
          },
          {
            label: 'Tube C2C:',
            value: boilerComponent.tubeSpacing?.convertTo(
              GeckoMeasurementUnit.Inches
            ),
          }
        )
      }
    } else {
      items.push(
        {
          label: 'Width:',
          value: componentModel?.componentBounds.width,
        },
        {
          label: 'Height:',
          value: componentModel?.componentBounds.height,
        }
      )
    }

    return items.map((item) => ({
      ...item,
      formattedValue:
        item.value instanceof GeckoMeasurement
          ? formatMeasurement({
              measurement: item.value,
              minimumFractionDigits:
                MEASUREMENT_FORMAT.DIMENSION_FRACTION_DIGITS.MIN,
              maximumFractionDigits:
                MEASUREMENT_FORMAT.DIMENSION_FRACTION_DIGITS.MAX,
              variant: 'narrow',
            })
          : item.value,
    }))
  }, [selectedAssetModel, componentModel, formatMeasurement])

  const staticDataItems = useMemo(() => {
    const customerTitle =
      formData.mainInfo.customerName ||
      `${organization?.data.name ?? ''} - ${site?.data.name ?? ''}`
    const customerLocation =
      formData.mainInfo.customerLocation ||
      (site?.data.address
        ? `${site.data.address.city}, ${site.data.address.region}`
        : '')
    const assetTypeTitle = formData.mainInfo.assetType
      ? ` - ${formData.mainInfo.assetType}`
      : ''
    const assetTitle = formData.mainInfo.assetName
      ? `${formData.mainInfo.assetName}${assetTypeTitle}`
      : unit?.data.name
        ? `${unit.data.name}${assetTypeTitle}`
        : ''

    return [
      { label: 'Client:', formattedValue: customerTitle },
      { label: 'Location:', formattedValue: customerLocation },
      { label: 'Asset:', formattedValue: assetTitle },
    ]
  }, [formData.mainInfo, organization, site, unit])

  const nominalDataItem: DataItem = {
    label: 'Nominal:',
    formattedValue: nominalRange
      ? formatMeasurementRange({
          measurement: nominalRange,
          minimumFractionDigits:
            MEASUREMENT_FORMAT.NOMINAL_RANGE_FRACTION_DIGITS.MIN,
          maximumFractionDigits:
            MEASUREMENT_FORMAT.NOMINAL_RANGE_FRACTION_DIGITS.MAX,
          variant: 'narrow',
        })
      : '',
  }

  const allDataItems = [...staticDataItems, ...dimensions, nominalDataItem]

  return (
    <View
      style={{ gap: SPACING.GAP.SMALL, marginBottom: SPACING.MARGIN.MEDIUM }}>
      <View
        style={{
          width: '100%',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignContent: 'center',
          gap: SPACING.GAP.SMALL,
        }}>
        {allDataItems.map((item, index) => (
          <DataSheetListedItem
            key={index}
            fieldName={item.label}
            fieldValue={item.formattedValue}
            flexDirection='column'
          />
        ))}
      </View>
    </View>
  )
}
